import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import useBranchApiStore from '@/stores/api/store';
import { formatTimeRange, formatPhoneNumber } from '@/utils/commonUtil';

const defaultBranchId = '1068'; // 죽전점 기본 지점

export const useRootStore = defineStore('root', {
    state: () => {
        return {
            defaultStoreInfo: { 
                areaName: '경기',
                storeId: defaultBranchId,
                name: '죽전점',
                lat: 37.3253071, // 위도
                lng: 127.1098259, // 경도
            },
            storeInfo: {},
            branchList: [],
            selectedBranchId: new URLSearchParams(window.location.search).get('branch') || defaultBranchId,
            facilityContents: '',
            isNaviVisible: true, // 네비게이션 표시 상태
            shareInfo: {},
        };
    },
    actions: {
        // 네비게이션 표시/숨김 설정
        setNaviVisible(visible) {
            this.isNaviVisible = visible;
        },
        resetStoreInfo() {
            this.storeInfo = {};
        },
        setShareInfo(info) {
            this.shareInfo = info;
        },
        // URL에서 branch 파라미터를 읽어서 selectedBranchId와 동기화
        syncBranchFromUrl() {
            const urlBranchId = new URLSearchParams(window.location.search).get('branch');
            if (urlBranchId && urlBranchId !== this.selectedBranchId) {
                // 브랜치가 변경되면 기존 storeInfo 초기화
                this.resetStoreInfo();

                if (this.branchList.length > 0) {
                    const isValidBranch = this.branchList.some((branch) => branch.branchId.toString() === urlBranchId.toString());
                    if (isValidBranch) {
                        this.selectedBranchId = urlBranchId;
                    }
                } else {
                    this.selectedBranchId = urlBranchId;
                }
            }
        },
        // URL 쿼리 파라미터 업데이트
        updateUrlQuery(key, value) {
            const currentUrl = new URL(window.location);
            if (value) {
                currentUrl.searchParams.set(key, value);
            } else {
                currentUrl.searchParams.delete(key);
            }
            window.history.replaceState({}, '', currentUrl.toString());
        },
        // 점포 id 변경 (URL 쿼리도 함께 업데이트)
        setSelectedBranchId(branchId) {
            // 브랜치가 변경되면 기존 storeInfo 초기화
            if (this.selectedBranchId !== branchId) {
                this.resetStoreInfo();
            }
            this.selectedBranchId = branchId;
            this.updateUrlQuery('branch', branchId);
        },
        // 점포 정보 조회
        async getStoreInfo() {
            // storeInfo가 이미 존재하면 API 호출하지 않음
            if (Object.keys(this.storeInfo).length > 0) {
                return;
            }

            const res = await useBranchApiStore().getStoreInfo({
                storeCode: this.selectedBranchId,
                yearMonth: dayjs().format('YYYYMM'),
            });

            if (res && res.status === 200) {
                const { openTime, closeTime, telNo } = res.data;
                const openingHours = formatTimeRange(openTime, closeTime);
                this.storeInfo = {
                    ...res.data,
                    openingHours,
                    telNo: formatPhoneNumber(telNo),
                };
            }
        },
        // 스타필드 정보조회
        async getStarFieldInfo(url) {
            const res = await useBranchApiStore().getStarFieldInfo(url);

            if (res && res.status === 200) {
                return res.data;
            }
        },
        // 점포 목록 조회
        async getBranchList() {
            const res = await this.getStarFieldInfo('/branch/list');

            if (res?.status === 200) {
                this.branchList = res.data.branchList;

                const urlParams = new URLSearchParams(window.location.search);
                const branchId = urlParams.get('branch');

                if (branchId) {
                    const isInBranchList = this.branchList.some((branch) => branch.branchId === Number(branchId));
                    if (!isInBranchList) {
                        // 유효하지 않은 branch인 경우 defaultBranchId로 설정 (URL도 함께 업데이트)
                        this.setSelectedBranchId(defaultBranchId);
                    }
                }
            }
        },
        // 점포별 편의시설
        async getFacility() {
            const res = await useBranchApiStore().getStarFieldInfo(`/facility/${this.selectedBranchId}`);

            if (res.data?.status === 200) {
                this.facilityContents = res.data.data?.facilityConts || '';
            }
        },
    },
    getters: {
        storeName(state) {
            let name = state.defaultStoreInfo.name;
            if (state.storeInfo?.storeName) {
                name = state.storeInfo.storeName;
            }
            return name;
        },
        storeLatLng(state) {
            let { lat, lng } = state.defaultStoreInfo;
            if (Object.keys(state.storeInfo).length) {
                lat = state.storeInfo.mapY;
                lng = state.storeInfo.mapX;
            }

            return { lat, lng };
        },
    },
});
